import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { ModalAtom } from '../../../../components/atoms/atom';
import { FilterBar } from "../../../../components/molecule/molecule";
import { AuthContext } from "../../../../contexts/AuthProvider";
import { setting } from '../../../../store/services/index';
import { getSearchAddFilter } from '../../../../utils/get-filter-items';
import { showError, showToast } from '../../../../utils/method';
import SettingsMenu from '../../settings-menu/settings-menu';
import AddPriorityModal from "./add-priority";
import PrioritySortableList from './priority-sortable-list';

function PriorityList() {
	const { processId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const selectedProcess = location.state?.process || { id: processId };
	const { t } = useTranslation();
	// eslint-disable-next-line no-unused-vars
	const { helpMode } = useContext(AuthContext);
	const [priorityModalView, setPriorityModalView] = useState('');
	const [selectedPriority, setSelectedPriority] = useState({});
	const [showDeleteModal, setShowDeleteModal] = useState(false);

	const filterItems = getSearchAddFilter(t, { addTextKey: 'add-priority' });

	const leftItems = [
		{
			type: 'backButton',
			key: 'back'
		},
		{
			type: 'button',
			key: 'add',
			disabled: true,
			label: <div className="d-flex">
				<p className="text-uppercase m-0">{selectedProcess?.name}</p>
			</div>,
			className: "selected-process filter-bar-btn"
		}
	];

	// eslint-disable-next-line no-unused-vars
	const [leftFilterItems, setLeftFilterItems] = useState(leftItems.concat(filterItems.left) || []);
	const [rightFilterItems] = useState(filterItems.right || []);
	const [priorityList, setPriorityList] = useState([]);

	const fetchPriorities = async () => {
		try {
			const result = await setting.getPrioritiesByProcessId(processId);
			setPriorityList(result?.data || []);
		} catch (error) {
			showError(t, error);
		}
	};

	useEffect(() => {
		fetchPriorities();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [processId]);

	const onPriorityEdit = (row) => {
		setSelectedPriority(row);
		setPriorityModalView('edit');
	};

	const onPriorityDelete = (row) => {
		setSelectedPriority(row);
		setShowDeleteModal(true);
	};

	const onAddPriority = async (priorityInfo) => {
		try {
			if (priorityModalView === 'add') {
				// New priority gets added at the end with next position
				const payload = {
					processId: processId,
					label: priorityInfo.label,
					priority: priorityList.length + 1,
				};
				const res = await setting.addPriority(payload);
				showToast(t(res?.message), { type: 'success' });
			} else if (priorityModalView === 'edit') {
				const res = await setting.updatePriority({
					label: priorityInfo.label,
				}, priorityInfo.id);
				showToast(t(res?.message), { type: 'success' });
			}
			fetchPriorities();
			setPriorityModalView('');
		} catch (error) {
			showToast(t(error?.message), { type: 'error' });
		}
	};

	const onListItemSortEnd = async (items) => {
		try {
			const payload = items.map((item, index) => ({
				priorityId: item?.id,
				priority: index + 1,
			}));
			const res = await setting.reorderPriorities(payload);
			showToast(t(res?.message), { type: 'success' });
			fetchPriorities();
		} catch (error) {
			showToast(t(error?.message), { type: 'error' });
		}
	};

	const onFilterAction = (index, key, info) => {
		if (key === 'add') {
			setSelectedPriority({});
			setPriorityModalView('add');
		}
		if (key === 'back') {
			navigate('/settings/process');
		}
	};

	const onDeletePriority = async () => {
		try {
			const res = await setting.deletePriority(selectedPriority?.id);
			showToast(t(res?.message), { type: 'success' });
			fetchPriorities();
			setShowDeleteModal(false);
		} catch (error) {
			showToast(t(error?.message), { type: 'error' });
		}
	};

	return (
		<>
			<SettingsMenu />
			<FilterBar
				leftItems={leftFilterItems}
				rightItems={rightFilterItems}
				onFilterAction={onFilterAction}
			/>

			<PrioritySortableList
				listItems={priorityList}
				onListItemSortEnd={onListItemSortEnd}
				onEdit={onPriorityEdit}
				onDelete={onPriorityDelete}
				t={t}
			/>

			{priorityModalView && <AddPriorityModal
				mode={priorityModalView}
				priorityInfo={selectedPriority}
				show
				onClose={() => { setPriorityModalView(''); setSelectedPriority({}); }}
				onSubmit={onAddPriority}
			/>}
			<ModalAtom
				title={t('delete-priority')}
				body={<p>{t('delete-priority-warning')}</p>}
				saveText="delete"
				show={showDeleteModal}
				handleClose={() => setShowDeleteModal(false)}
				handleSubmit={onDeletePriority}
			/>
		</>
	);
}

export default PriorityList;
